import React, { useEffect, useState } from 'react';
import '../../onboarding/components/OnboardingOptionCard.css';
import '../../onboarding/components/OnboardingSummaryCard.css';
import PlanItemRow from './PlanItemRow';
import useCapabilityNames from '../hooks/useCapabilityNames';
import { actionCounts, groupItemsByPhase, showsPhaseHeadings, WORKLOAD_LABELS } from '../utils/phaseStatus';
import { displayText, displayTextList } from '../utils/displayText';
import { formatPlanDate } from '../utils/planDates';
import './PlanItemRow.css';
import './DraftPlanCard.css';

// Structured draft-plan preview, built to the onboarding-card contract:
// payload + callbacks, zero network calls inside the component (spec
// §10.3, brief §10.3's read-across from OnboardingSummaryCard). Rendered
// as a sibling below the chat transcript, never embedded in a chat message
// — chat_messages has only a `message TEXT` column and the markdown
// renderer has no bullet lists or tables, so a plan cannot be conveyed as
// text (brief §10.3).
//
// Ordering: the plan items must be immediately visible, not below the
// fold behind a full spec — a card that shows goal/success-criteria/
// feasibility/warnings/assumptions before a single item reads as a
// specification document, not a plan. So: title + goal (one line each) ->
// THE PLAN -> target timing -> everything else, collapsed behind a closed
// disclosure. All of it is still visibly reachable (acceptance criteria:
// "important assumptions are not silently represented as facts") — just
// one click away instead of in front of the plan.
//
// version: { summary, assumptions: [], constraints_considered: [],
//   feasibility: {summary, warnings}, warnings: [], phases: [] }
//   Items are grouped under version.phases when there is more than one;
//   a one-phase plan shows a flat list. Every free-text field goes through
//   displayText, since the validator does not check that list entries are
//   strings and an object must never render as text.
// path: { title, goal, success_criteria, target_date, target_horizon }
// items: [{ id, title, instruction, reason, completion_condition,
//   target_date, window_start, window_end, estimated_effort_minutes,
//   capability_key }]
// onAccept(), onRequestChange() — focuses the composer for the user to
//   type a change request; no direct-edit UI in Phase 1 (spec §5.7).
// onDiscard()
// mode: 'draft' (default — a brand-new plan) |
//   'revision' (a revision of an existing accepted plan; both are shown by
//   PathsChatSurface below the conversation).
//   Only changes the eyebrow label ("Draft plan" vs "Edit plan"); the
//   accept/discard/ask-for-changes contract is identical either way.

function formatDate(dateStr) {
  if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
}

function chipDate(value) {
  if (value == null || value === '') return 'unset';
  return formatPlanDate(String(value).slice(0, 10)) || String(value).slice(0, 10);
}

function wasLine(entry) {
  if (!entry) return null;
  if (entry.field === 'title') return `Was ${entry.value}`;
  if (entry.field === 'target_date') return `Was due ${chipDate(entry.value)}`;
  if (entry.field === 'window') return `Was ${chipDate(entry.start)} – ${chipDate(entry.end)}`;
  if (entry.field === 'minutes') return `Was ${entry.value} min`;
  if (entry.field === 'app') return `Was ${entry.value || 'no app'}`;
  if (entry.field === 'phase') return `Was in ${entry.value}`;
  if (entry.field === 'details') return 'Details changed';
  return null;
}

function DraftPlanCard({
  path, version, items, onAccept, onRequestChange, onDiscard, accepting, mode = 'draft',
  changes = null, readOnly = false, showChanges,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [hideChanges, setHideChanges] = useState(false);
  const versionId = version?.id;
  useEffect(() => { setHideChanges(false); }, [versionId]);
  const controlled = typeof showChanges === 'boolean';
  const markersOn = controlled ? showChanges : !hideChanges;
  const capabilityNames = useCapabilityNames();

  const assumptions = displayTextList(version?.assumptions);
  const constraints = displayTextList(version?.constraints_considered);
  const feasibility = version?.feasibility && typeof version.feasibility === 'object' ? version.feasibility : {};
  const feasibilitySummary = displayText(feasibility.summary);
  const warnings = [...displayTextList(version?.warnings), ...displayTextList(feasibility.warnings)];
  const successCriteria = displayText(path?.success_criteria);

  const timing = formatDate(path?.target_date) || displayText(path?.target_horizon) || null;

  const detailCount = warnings.length + assumptions.length + constraints.length + (feasibilitySummary ? 1 : 0);
  const itemList = Array.isArray(items) ? items : [];
  const groups = groupItemsByPhase(version?.phases, itemList);
  const withHeadings = showsPhaseHeadings(groups);
  const actionTotal = actionCounts(itemList).total;
  const diff = markersOn ? changes?.diff : null;
  const itemMarks = diff?.items || {};
  const phaseMarks = diff?.phases || {};
  const renderRows = (list) => (
    <div className="draft-plan-items">
      {list.map((item) => {
        const mark = itemMarks[item.id];
        return (
          <div key={item.id || item.local_id} className={mark ? `draft-plan-marked draft-plan-marked-${mark.mark}` : undefined}>
            {mark?.mark === 'new' && <span className="draft-plan-tag">NEW</span>}
            {mark?.mark === 'changed' && <span className="draft-plan-tag">CHANGED</span>}
            <PlanItemRow item={item} interactive={false} capabilityNames={capabilityNames} />
            {mark?.was?.map((entry) => {
              const line = wasLine(entry);
              return line ? <p key={entry.field} className="draft-plan-was">{line}</p> : null;
            })}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="onboarding-card draft-plan-card">
      <div className="draft-plan-header">
        <div className="onboarding-eyebrow">{mode === 'revision' ? 'Edit plan' : 'Draft plan'}</div>
        <h3 className="draft-plan-title">{displayText(path?.title) || 'Your draft plan'}</h3>
        {displayText(path?.goal) && <p className="draft-plan-goal">{displayText(path.goal)}</p>}
      </div>

      <div className="draft-plan-section draft-plan-section-scroll">
        <div className="onboarding-eyebrow">
          Plan ({actionTotal} {actionTotal === 1 ? 'action' : 'actions'})
        </div>
        {itemList.length === 0 && <p className="draft-plan-text">This draft has no items.</p>}
        {!withHeadings && groups.map((group) => (
          <React.Fragment key={group.phase?.id || 'unphased'}>{renderRows(group.items)}</React.Fragment>
        ))}
        {withHeadings && groups.map((group, index) => {
          const name = displayText(group.phase?.name) || 'Other items';
          const outcome = displayText(group.phase?.outcome);
          const workload = WORKLOAD_LABELS[group.phase?.workload];
          const phaseMark = phaseMarks[group.phase?.id];
          const removedHere = (diff?.removed || []).filter((item) => item.phase_id === group.phase?.id);
          return (
            <div className="draft-plan-phase" key={group.phase?.id || 'unphased'}>
              <div className="draft-plan-phase-head">
                <span className="draft-plan-phase-name">{index + 1} · {name}</span>
                {phaseMark?.mark === 'new' && <span className="draft-plan-tag">NEW</span>}
                {workload && <span className="plan-item-chip">{workload}</span>}
              </div>
              {phaseMark?.mark === 'changed' && phaseMark.was_position != null && (
                <p className="draft-plan-was">Was {phaseMark.was_position} · {phaseMark.was_name}</p>
              )}
              {phaseMark?.details && <p className="draft-plan-was">Details changed</p>}
              {outcome && <p className="draft-plan-phase-outcome">{outcome}</p>}
              {renderRows(group.items)}
              {removedHere.map((item) => (
                <p key={item.title} className="draft-plan-removed">Removed · {item.title}</p>
              ))}
            </div>
          );
        })}
        {(diff?.removed_phases || []).map((phase) => (
          <p key={phase.id || phase.name} className="draft-plan-removed">Removed phase · {phase.name}</p>
        ))}
      </div>

      {timing && (
        <div className="draft-plan-timing draft-plan-timing-fixed">
          <span className="draft-plan-timing-label">Target:</span> {timing}
        </div>
      )}

      {detailCount > 0 && (
        <details
          className="draft-plan-details"
          open={detailsOpen}
          onToggle={(e) => setDetailsOpen(e.target.open)}
        >
          <summary className="draft-plan-details-summary">
            Assumptions and warnings ({detailCount})
          </summary>

          {successCriteria && (
            <div className="draft-plan-section">
              <div className="onboarding-eyebrow">Success criteria</div>
              <p className="draft-plan-text">{successCriteria}</p>
            </div>
          )}

          {feasibilitySummary && (
            <div className="draft-plan-section">
              <div className="onboarding-eyebrow">Feasibility</div>
              <p className="draft-plan-text">{feasibilitySummary}</p>
            </div>
          )}

          {warnings.length > 0 && (
            <div className="draft-plan-section draft-plan-warnings">
              <div className="onboarding-eyebrow">Warnings</div>
              <ul className="draft-plan-list">
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          {assumptions.length > 0 && (
            <div className="draft-plan-section">
              <div className="onboarding-eyebrow">Assumptions</div>
              <ul className="draft-plan-list">
                {assumptions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {constraints.length > 0 && (
            <div className="draft-plan-section">
              <div className="onboarding-eyebrow">Constraints considered</div>
              <ul className="draft-plan-list">
                {constraints.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}
        </details>
      )}

      {!controlled && changes?.diff && !readOnly && (
        <button type="button" className="onboarding-btn-quiet draft-plan-hide" onClick={() => setHideChanges((v) => !v)}>
          {hideChanges ? 'Show changes' : 'Hide changes'}
        </button>
      )}

      <div className="onboarding-card-foot onboarding-card-foot-readonly">
        {readOnly ? (
          <p className="draft-plan-readonly">Available when the update finishes</p>
        ) : (
        <>
        <button type="button" className="onboarding-btn-quiet" onClick={onDiscard} disabled={accepting}>
          Discard
        </button>
        <button type="button" className="onboarding-btn-quiet" onClick={onRequestChange} disabled={accepting}>
          Ask for changes
        </button>
        <button type="button" className="onboarding-btn-primary" onClick={onAccept} disabled={accepting}>
          {accepting ? 'Accepting…' : 'Accept plan'}
        </button>
        </>
        )}
      </div>
    </div>
  );
}

export default DraftPlanCard;
